import * as runtime from '../runtime';
import type {
  ErrorResponse,
  GetConversationMessagesResponse,
  GetConversationsResponse,
  GetOrCreateConversationPayload,
  GetOrCreateConversationResponse,
  SendMessagePayload,
  SendMessageResponse,
  UpdateConversationPayload,
  UpdateConversationResponse,
} from '../models';

export interface GetConversationMessagesRequest {
  id: number;
}

export interface GetOrCreateConversationRequest {
  getOrCreateConversationPayload: GetOrCreateConversationPayload;
}

export interface SendMessageRequest {
  sendMessagePayload: SendMessagePayload;
}

export interface UpdateConversationRequest {
  id: number;
  updateConversationPayload: UpdateConversationPayload;
}

/**
 *
 */
export class MessagesApi extends runtime.BaseAPI {
  async getConversationMessagesRaw(
    requestParameters: GetConversationMessagesRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<GetConversationMessagesResponse>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        'id',
        'Required parameter requestParameters.id was null or undefined when calling getConversationMessages.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/messages/conversations/{id}/messages`.replace(
          `{${'id'}}`,
          encodeURIComponent(String(requestParameters.id)),
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Retrieves all messages in a specific conversation. Automatically marks messages as read for the current user. Messages are sorted by creation date in ascending order (oldest first).
   * Get all messages in a conversation
   */
  async getConversationMessages(
    requestParameters: GetConversationMessagesRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<GetConversationMessagesResponse> {
    const response = await this.getConversationMessagesRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async getConversationsRaw(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<GetConversationsResponse>> {
    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    const response = await this.request(
      {
        path: `/messages/conversations`,
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Retrieves all conversations where the current user is either a buyer or seller. Conversations are sorted by most recent message date in descending order.
   * List all conversations for current user
   */
  async getConversations(
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<GetConversationsResponse> {
    const response = await this.getConversationsRaw(initOverrides);
    return await response.value();
  }

  async getOrCreateConversationRaw(
    requestParameters: GetOrCreateConversationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<GetOrCreateConversationResponse>> {
    if (
      requestParameters.getOrCreateConversationPayload === null ||
      requestParameters.getOrCreateConversationPayload === undefined
    ) {
      throw new runtime.RequiredError(
        'getOrCreateConversationPayload',
        'Required parameter requestParameters.getOrCreateConversationPayload was null or undefined when calling getOrCreateConversation.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/messages/conversations`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.getOrCreateConversationPayload,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Retrieves an existing conversation or creates a new one between a buyer and a seller. This is the entry point for starting a messaging conversation.
   * Get or create a conversation with a seller
   */
  async getOrCreateConversation(
    requestParameters: GetOrCreateConversationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<GetOrCreateConversationResponse> {
    const response = await this.getOrCreateConversationRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async sendMessageRaw(
    requestParameters: SendMessageRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<SendMessageResponse>> {
    if (
      requestParameters.sendMessagePayload === null ||
      requestParameters.sendMessagePayload === undefined
    ) {
      throw new runtime.RequiredError(
        'sendMessagePayload',
        'Required parameter requestParameters.sendMessagePayload was null or undefined when calling sendMessage.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/messages/send`,
        method: 'POST',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.sendMessagePayload,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Sends a new message in an existing conversation. The sender must be a participant (buyer or seller) in the conversation.
   * Send a message in a conversation
   */
  async sendMessage(
    requestParameters: SendMessageRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<SendMessageResponse> {
    const response = await this.sendMessageRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }

  async updateConversationRaw(
    requestParameters: UpdateConversationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<runtime.ApiResponse<UpdateConversationResponse>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        'id',
        'Required parameter requestParameters.id was null or undefined when calling updateConversation.',
      );
    }

    if (
      requestParameters.updateConversationPayload === null ||
      requestParameters.updateConversationPayload === undefined
    ) {
      throw new runtime.RequiredError(
        'updateConversationPayload',
        'Required parameter requestParameters.updateConversationPayload was null or undefined when calling updateConversation.',
      );
    }

    const queryParameters: Record<
      string,
      string | string[] | number | boolean
    > = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request(
      {
        path: `/messages/conversations/{id}`.replace(
          `{${'id'}}`,
          encodeURIComponent(String(requestParameters.id)),
        ),
        method: 'PATCH',
        headers: headerParameters,
        query: queryParameters,
        body: requestParameters.updateConversationPayload,
      },
      initOverrides,
    );

    return new runtime.JSONApiResponse(response);
  }

  /**
   * Updates a conversation\'s status (e.g., archive or reactivate). Only the seller can update conversation status.
   * Update conversation status
   */
  async updateConversation(
    requestParameters: UpdateConversationRequest,
    initOverrides?: RequestInit | runtime.InitOverrideFunction,
  ): Promise<UpdateConversationResponse> {
    const response = await this.updateConversationRaw(
      requestParameters,
      initOverrides,
    );
    return await response.value();
  }
}
